//-- Copyright (c) 2021 TMT Insights Group, LLC
import { DirectLink, NavLinks } from '@/__configs/config_types';
import { convertStylesToStyled } from '@/_utils/utils';
import { selectIsAuthenticated, selectPermissions } from '@/redux/selectors/authSelectors';
import { selectDashboardIsLoading } from '@/redux/selectors/dashboardSelectors';
import { AuthState } from '@/redux/slices/slice_types';
import styled from '@emotion/styled';
import { Box, Container, Divider, Grid, IconButton, useTheme } from '@mui/material';
import React, { Suspense, useEffect, useState } from 'react';
import { NavLink, Route } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';
import defaultLogo from '../../assets/images/tmt_logo_hor.png';
import PolarisIcon from '../_common/PolarisIcon';
import LoadingBar from '../_common/LoadingBar';
import ClientToggle from './ClientToggle';
import NavDropdown from './NavDropdown';
import NotificationsPopout from './NotificationsPopout/NotificationsPopout';
import NotificationsPopoutModal from './NotificationsPopout/NotificationsPopoutModal';
import SidebarMobile from './SidebarMobile';
import ThemeToggle from './ThemeToggle';
import { useConfigValue } from '../_hooks/useConfigValue';
import { NAVBAR_HEIGHT, NAVBAR_HIDE_TRANSITION_DURATION, Roles } from '@/constants/constants';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import FocusLogo from '../Focus/configs/assets/focus.svg';
import { selectIsNavBarVisible } from '@/redux/selectors/layoutSelectors';
import { setIsNavBarVisible } from '@/redux/slices/layoutSlice';

const UserMenu = React.lazy(() => import('./UserMenu'));

export const defaultLinks: NavLinks = [
  { label: 'Dashboard', id: 'dashboard', exact: true, to: '/', role: Roles.view_dashboard },
  { label: 'Orders', id: 'orders', exact: false, to: '/orders', role: Roles.view_orders },
  { label: 'Jobs', id: 'jobs', exact: false, to: '/jobs', role: Roles.view_jobs },
  {
    label: 'Task Manager',
    exact: false,
    id: 'tasks',
    to: '',
    children: [
      { label: 'Tasks', to: '/tasks', role: Roles.view_tasks },
      { label: 'Pending Assets', to: '/pending-assets', role: Roles.view_order_job_assets },
      { label: 'Processing Exceptions', to: '/processing-exceptions', role: Roles.view_tasks }
    ]
  },
  {
    label: 'Exceptions',
    id: 'exceptions',
    exact: true,
    to: '/exceptions',
    role: Roles.view_event
  },
  {
    label: 'Ingest Manager',
    id: 'ingest-management',
    exact: true,
    to: '/ingest-management',
    role: Roles.view_fileingest
  },
  { label: 'Titles', id: 'titles', exact: false, to: '/titles', role: Roles.view_titles },
  {
    label: 'File Manager',
    id: 'fileManager',
    exact: true,
    to: '/file-manager',
    role: Roles.view_filemanager
  },
  {
    label: '',
    exact: false,
    id: 'focus',
    to: '/focus/schedule',
    role: Roles.view_avail
  }
];

const focusNavLinks: NavLinks = [
  {
    label: 'Schedule',
    id: 'schedule',
    exact: true,
    to: '/focus/schedule',
    role: Roles.view_schedule
  },
  {
    label: 'Avails',
    id: 'avails',
    exact: true,
    to: '/focus/avails',
    role: Roles.view_avail
  },
  {
    label: 'Releases',
    id: 'releases',
    exact: true,
    to: '/focus/releases',
    role: Roles.view_releases
  },
  {
    label: 'Reports',
    id: 'reports',
    exact: true,
    to: '/focus/reports',
    role: Roles.view_avail
  },
  {
    label: '',
    id: 'polaris',
    exact: true,
    to: '/',
    role: Roles.view_avail
  }
];
const getNavbarLinks = (permissions: AuthState['permissions'], navLinksConfig: NavLinks, isFocus?: boolean) => {
  if (!permissions?.length) return [];

  const allowedNavLinks: NavLinks = [];
  const navLinks = isFocus ? focusNavLinks : navLinksConfig || defaultLinks;
  navLinks.forEach((group) => {
    if (group.internal) return;
    if (group.children) {
      const filteredChildren =
        group.children?.filter((children: DirectLink) => !children.role || (permissions && permissions.includes(children.role))) ?? [];
      group.children = filteredChildren;
      if (filteredChildren.length) {
        allowedNavLinks.push(group);
      }
    } else if (group.role) {
      if (permissions && permissions.includes(group.role)) allowedNavLinks.push(group);
    } else {
      console.error('Role not set for ', group);
    }
  });
  return allowedNavLinks;
};

const Navbar = ({
  configOptions,
  animateFocus,
  reverseAnimateFocus,
  isFocus
}: {
  configOptions?: any[];
  animateFocus?: boolean;
  reverseAnimateFocus?: boolean;
  isFocus?: boolean;
}) => {
  const dispatch = useAppDispatch();
  const dashboardLoading = useAppSelector(selectDashboardIsLoading);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const devEnv = process.env.NODE_ENV === 'development';
  const permissions = useAppSelector(selectPermissions);
  const isNavBarVisible = useAppSelector(selectIsNavBarVisible);
  const navLinksConfig = useConfigValue(['navLinks']);
  const navLinks = getNavbarLinks(permissions, navLinksConfig, isFocus);
  const logo = isFocus ? FocusLogo : useConfigValue(['theme', 'logo']) || defaultLogo;
  const notifications = useConfigValue(['notifications']);
  const clientName = useConfigValue(['clientName']) || "Customer's";
  const [sidebarMobileOpen, setSidebarMobileOpen] = useState(false);
  const [showSettingsModal, setSettingsModalVisibility] = useState(false);
  const [settingsModalDefaultTab, setSettingsModalDefaultTab] = useState<number | undefined>();
  const [showNotificationsModal, setShowNotificationsModal] = useState(false);
  const theme = useTheme();
  const activeStyling = `
    background: #353C504a;
    border-radius: ${theme.shape.borderRadius}px;
    opacity: 1;
    font-weight: 900;
  `;

  const direction = isNavBarVisible ? 'up' : 'down';

  const onShowNotificationSettings = () => {
    setSettingsModalDefaultTab(2);
    setSettingsModalVisibility(true);
    setShowNotificationsModal(false);
  };

  const onToggleSettingsModal = (modalState: boolean) => {
    setSettingsModalVisibility(modalState);
    if (!modalState) setSettingsModalDefaultTab(0);
  };

  useEffect(() => {
    let lastScroll = 0;
    const handleScroll = () => {
      const currentScroll = window.scrollY;
      dispatch(setIsNavBarVisible(currentScroll - lastScroll > 0 && currentScroll > NAVBAR_HEIGHT ? false : true));
      lastScroll = currentScroll;
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const renderNavContent = () => {
    return (
      <>
        <NavLink to={isFocus ? '/focus/schedule' : '/'}>
          <Logo>
            <img src={logo} alt={`${clientName} logo`} />
          </Logo>
        </NavLink>

        {isAuthenticated && (
          <NavList>
            {navLinks.map((navLink) => {
              if (!navLink.children) {
                const linkTo =
                  navLink.id === 'focus'
                    ? { pathname: (navLink as DirectLink).to, state: { animateFocus: true } }
                    : navLink.id === 'polaris'
                      ? { pathname: (navLink as DirectLink).to, state: { reverseAnimateFocus: true } }
                      : { pathname: (navLink as DirectLink).to };
                if (navLink.internal === false)
                  return (
                    <a key={`navlink-${navLink.id}`} href={linkTo.pathname}>
                      <Grid container alignItems="center">
                        {navLink.label}
                      </Grid>
                    </a>
                  );

                return (
                  <StyledNavLink
                    key={`navlink-${navLink.id}`}
                    activeClassName={'active'}
                    activestyling={activeStyling}
                    exact={navLink.exact}
                    to={linkTo}
                    id={'mainNav' + (navLink.id || '')}
                  >
                    <Grid container alignItems="center">
                      {navLink.label}
                    </Grid>
                  </StyledNavLink>
                );
              }

              return (
                <NavDropdown
                  key={`navlink-${navLink.id}`}
                  id={navLink.id || ''}
                  label={navLink.label}
                  items={navLink.children as DirectLink[]}
                  activeStyling={activeStyling}
                />
              );
            })}
          </NavList>
        )}
      </>
    );
  };

  return (
    <div>
      <Header direction={direction}>
        <SidebarMobile open={sidebarMobileOpen} setOpen={setSidebarMobileOpen} navLinks={navLinks} />
        <Container>
          <StyledGrid container>
            {animateFocus && (
              <NavStyles>
                <CSSTransition in={animateFocus} timeout={1000} classNames="nav-focus" appear>
                  <Nav>{renderNavContent()}</Nav>
                </CSSTransition>
              </NavStyles>
            )}

            {reverseAnimateFocus && (
              <NavStyles>
                <CSSTransition in={reverseAnimateFocus} timeout={1000} classNames="nav-reverse-focus" appear>
                  <Nav>{renderNavContent()}</Nav>
                </CSSTransition>
              </NavStyles>
            )}

            {!animateFocus && !reverseAnimateFocus && <Nav>{renderNavContent()}</Nav>}

            {isAuthenticated && (
              <UserList isFocus={isFocus}>
                <Box component="section" sx={{ display: 'flex', alignItems: 'center' }}>
                  {devEnv && configOptions && configOptions.length > 1 && (
                    <li>
                      <ClientToggle configOptions={configOptions} />
                    </li>
                  )}
                  {devEnv && (
                    <>
                      <li>
                        <ThemeToggle />
                      </li>
                      <Divider orientation="vertical" flexItem sx={{ mr: 1.25, ml: 1, backgroundColor: 'rgba(255,255,255,.02)' }} />
                    </>
                  )}
                </Box>
                {notifications && (
                  <li>
                    <NotificationsPopout
                      onShowNotificationSettings={onShowNotificationSettings}
                      onShowModal={() => setShowNotificationsModal(true)}
                    />
                    <NotificationsPopoutModal
                      onShowNotificationSettings={onShowNotificationSettings}
                      showNotificationsModal={showNotificationsModal}
                      onClose={() => setShowNotificationsModal(false)}
                    />
                  </li>
                )}
                <li>
                  <Suspense fallback={<PolarisIcon icon="loading" height={30} />}>
                    <UserMenu
                      defaultUserSettingsTab={settingsModalDefaultTab}
                      showSettingsModal={showSettingsModal}
                      onToggleSettingsModal={onToggleSettingsModal}
                    />
                  </Suspense>
                </li>
                <li>
                  <HamburgerMenuButton
                    edge="start"
                    size="large"
                    sx={{ mt: -1, mb: -1, ml: 1, display: { md: 'none' } }}
                    onClick={() => setSidebarMobileOpen(true)}
                  >
                    <PolarisIcon icon="menu" />
                  </HamburgerMenuButton>
                </li>
              </UserList>
            )}
          </StyledGrid>
        </Container>
      </Header>
      <Route path={['/', '/dashboard']} exact>
        <StyledLoadingBar loading={dashboardLoading} direction={direction} />
      </Route>
    </div>
  );
};

export default Navbar;

const StyledGrid = styled(Grid)`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: space-between;
  align-text: center;
`;

const Header = styled.header<{ direction: 'up' | 'down' }>`
  height: ${NAVBAR_HEIGHT}px;
  width: 100vw;
  padding: 0;
  margin: 0;
  z-index: 1000;
  position: fixed;
  transform: translateY(${({ direction }) => (direction === 'up' ? '0' : '-100%')});
  transition: transform ${NAVBAR_HIDE_TRANSITION_DURATION}ms ease;
  display: flex;
  align-items: center;
  ${({ theme }) => convertStylesToStyled(theme.customOverrides?.Header)}
`;

const NavStyles = styled.div`
  .nav-focus-appear,
  .nav-focus-enter {
    opacity: 0;
    transform: translateX(250%);
  }

  .nav-focus-appear-active,
  .nav-focus-enter-active {
    opacity: 1;
    transform: translateX(0);
    transition:
      opacity 0.1s ease-in,
      transform 1s ease-out;
  }

  .nav-focus-exit {
    opacity: 1;
    transform: translateX(0);
  }

  .nav-focus-exit-active {
    opacity: 1;
    transform: translateX(100%);
    transition: transform 1s ease-in;
  }

  .nav-reverse-focus-appear,
  .nav-reverse-focus-enter {
    opacity: 1;
    transform: translateX(0);
  }

  .nav-reverse-focus-appear-active,
  .nav-reverse-focus-enter-active {
    opacity: 1;
    transform: translateX(250%);
    transition: transform 1s ease-out;
  }

  .nav-reverse-focus-exit {
    opacity: 1;
    transform: translateX(0);
  }

  .nav-reverse-focus-exit-active {
    opacity: 1;
    transform: translateX(250%);
    transition: transform 1s ease-in;
  }
`;

const Nav = styled.nav`
  display: inline-flex;
  align-items: center;
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
`;

const Logo = styled.h1`
  display: inline-flex;
  align-items: center;
  float: left;
  color: white;

  & > img {
    height: 25px;
    padding-right: 20px;
    & path {
      fill: white;
    }
  }
`;

const NavList = styled.ul`
  display: flex;
  align-items: center;
  list-style-type: none;
  padding: ${({ theme }) => theme.spacing(1)};
  padding-left: ${({ theme }) => theme.spacing(3)};
  margin: 0;
  min-height: 51px;

  & a,
  & button {
    position: relative;
    padding: ${({ theme }) => theme.spacing(1, 3)};
    opacity: 0.7;
    color: #fff;
    font-size: 1rem;
    text-decoration: none;
    transition: ease opacity 600ms;
    text-align: center;
    font-weight: ${({ theme }) => theme.typography.fontWeightNormal};

    &:hover {
      opacity: 1;
      transition: ease opacity 600ms;
    }

    ${({ theme }) => theme.breakpoints.only('md')} {
      padding: ${({ theme }) => theme.spacing(1, 2)};
    }
  }

  ${({ theme }) => theme.breakpoints.only('md')} {
    padding-left: 0;
    padding-right: 0;
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    display: none;
  }
`;

const UserList = styled.ul<{ isFocus?: boolean }>`
  display: flex;
  align-items: center;
  list-style-type: none;
  margin: 0;
  padding: 0 16px;

  & > li {
    margin-left: 12px;
  }

  ${({ isFocus }) =>
    isFocus &&
    `
    background-color: 'var(--default-dark)';
    position: absolute;
    top: 0;
    right: 0;
    height: 100%;
    padding-left: 40px;
  `}
`;

const HamburgerMenuButton = styled(IconButton)`
  display: flex;
  color: white;
`;

const StyledLoadingBar = styled(LoadingBar)<{ direction: 'up' | 'down' }>`
  z-index: 999;
  top: ${({ direction }) => (direction === 'up' ? `${NAVBAR_HEIGHT}px` : '0px')};
`;

const StyledNavLink = styled(NavLink)<{ activestyling: string }>`
  white-space: nowrap;
  &.${(props) => props.activeClassName} {
    ${({ activestyling }) => activestyling}
  }
`;
